import React from "react";
import { useTranslation } from "react-i18next";
import { Star } from "lucide-react";
import { Checkbox } from "@shadcn-in-nx/ui/checkbox";
import { TableCell, TableRow } from "@shadcn-in-nx/ui/table";
import InternalLink from "../../../components/InternalLink";
import { formatDate, getWorkflowLastUsageMessage } from "@utils";
import ActionsMenu from "../actions-menu";

export default function WorkflowRow({
    workflow,
    selected,
    onSelect,
    isFavorite,
    onToggleFavorite,
    onArchive,
    onCopied,
    interfaceStyle,
}) {
    const { t } = useTranslation();
    const usageMsg = getWorkflowLastUsageMessage(workflow.id);
    const isExpert =
        interfaceStyle === "expert" && workflow.workflowType !== "APP_V2";
    const linkTo = isExpert
        ? `/workflows/${workflow.id}/`
        : `/chat/${workflow.id}/chat`;

    return (
        <TableRow>
            <TableCell className="w-8">
                <Checkbox
                    checked={selected}
                    onCheckedChange={() => onSelect(workflow.id)}
                />
            </TableCell>
            <TableCell className="w-8">
                <button
                    type="button"
                    onClick={() => onToggleFavorite(workflow.id)}
                    className="hover:scale-110 transition-transform"
                    aria-label={
                        isFavorite
                            ? "Remove from favorites"
                            : "Add to favorites"
                    }
                >
                    <Star
                        className={`h-4 w-4 transition-colors duration-200 ${
                            isFavorite
                                ? "fill-yellow-400 text-yellow-400"
                                : "text-gray-400 hover:text-yellow-400"
                        }`}
                    />
                </button>
            </TableCell>
            <TableCell>
                <div className="space-y-1">
                    <div className="flex items-center gap-2 flex-wrap">
                        <InternalLink
                            to={linkTo}
                            preserveQuery={true}
                            className="text-blue-600 text-base font-medium"
                        >
                            {workflow.name}
                        </InternalLink>
                        {usageMsg && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
                                {usageMsg}
                            </span>
                        )}
                    </div>
                    <div className="sm:hidden flex flex-wrap gap-2 text-xs text-gray-500">
                        <span>
                            {t("workflowIndex.table.statusLabel")}{" "}
                            {workflow.status}
                        </span>
                        <span>•</span>
                        <span>
                            {workflow._count.steps}{" "}
                            {t("workflowIndex.table.stepsCount")}
                        </span>
                    </div>
                </div>
            </TableCell>
            <TableCell className="hidden sm:table-cell">
                {workflow.status}
            </TableCell>
            <TableCell className="hidden md:table-cell">
                {formatDate(new Date(workflow.createdAt))}
            </TableCell>
            <TableCell className="hidden sm:table-cell">
                {workflow._count.steps}
            </TableCell>
            <TableCell className="hidden lg:table-cell">
                {workflow._count.executions}
            </TableCell>
            <TableCell className="text-right">
                <ActionsMenu workflow={workflow} onArchive={onArchive} onCopied={onCopied} />
            </TableCell>
        </TableRow>
    );
}
